import React, { useState, useContext } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Button,
  FormControl,
  VisuallyHidden,
  Input,
  Box,
  useToast,
  Text,
  Flex,
  Image,
  Link,
  VStack,
} from '@chakra-ui/react';
import { UserContext } from './UserContext';
import './App.css'; // Ensure the CSS is imported for class-based styling
import {authReturn, authDestination} from './client/authContinuation';
import ProviderButtons, {providerName} from './auth/ProviderButtons';
import {completeBrowserSession} from './auth/socialAuth';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const linkingProvider = new URLSearchParams(window.location.search).get('linking');
  const linking = ['google','github'].includes(linkingProvider) ? providerName(linkingProvider) : null;
  const toast = useToast();
  const navigate = useNavigate();
  const { setUserDetails } = useContext(UserContext);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const clientReturn=authReturn();
      const apiUrl = '/api/login';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
  
      const data = await response.json();
      if (response.ok) {
        await completeBrowserSession(data.token, clientReturn || '/brief', setUserDetails, navigate);
  
        toast({
          title: 'Login Successful',
          description: 'You have been logged in successfully.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
  
      } else {
        toast({
          title: 'Login Failed',
          description: data.error || 'Login failed.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally { setBusy(false); }
  };

  return (
    <Flex className="start-container" direction={{ base: 'column', md: 'row' }}>
      <Box flex="1.5" minW={0} pr={{ base: 0, md: '5%' }}>
        <Link as={RouterLink} to="/"><Image src="/images/pablo_logo1.0.svg" alt="Pabs home" boxSize="48px" objectFit="contain" mb="5%" /></Link>
        <Text as="h1" fontSize={{ base: '4xl', md: '6xl' }} fontWeight="bold" letterSpacing="tight" mb={4} lineHeight={1.1} color="brand.900">
          {linking ? `Sign in to connect ${linking}` : 'Let’s make your next move count.'}
        </Text>
        {linking && <Text fontSize="lg" color="gray.700" maxW="65ch">
          {`${linking} isn’t connected to this Pabs account yet. Sign in using your existing method, then confirm the connection.`}
        </Text>}
      </Box>

      <Box
        className="container-box" minW={0} flex={{ base: "none", md: "1" }} mt={{ base: 6, md: '5%' }}>
        <ProviderButtons inset />
        <form onSubmit={handleLogin} className="fed-form auth-login-form">
          <VStack align="stretch" spacing={5}>
            <FormControl id="login-email" isRequired>
              <VisuallyHidden as="label" htmlFor="login-email">Email</VisuallyHidden>
              <Input
                name="email"
                placeholder="Enter your email"
                type="email"
                autoComplete="email"
                bg="white"
                _placeholder={{ color: 'gray.600' }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="fed-input"
              />
            </FormControl>
            <FormControl id="login-password" isRequired>
              <VisuallyHidden as="label" htmlFor="login-password">Password</VisuallyHidden>
              <Input
                name="password"
                placeholder="Password"
                type="password"
                autoComplete="current-password"
                bg="white"
                _placeholder={{ color: 'gray.600' }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="fed-input"
              />
            </FormControl>
            <Flex justify="flex-end">
              <Link
                as={RouterLink}
                to="/reset-password"
                state={{ email }}
                fontSize="sm"
                color="gray.700"
                textDecoration="underline"
              >
                Forgot password?
              </Link>
            </Flex>
            <Button colorScheme="brand" type="submit" width="100%" isLoading={busy} loadingText="Signing in…">
              {linking ? 'Sign in and continue' : 'Sign in'}
            </Button>
            {!linking && <Text fontSize="sm">New to Pabs? <Link as={RouterLink} to={authDestination('/register')} textDecoration="underline">Create an account</Link></Text>}
          </VStack>
          </form>
      </Box>
    </Flex>
  );
};

export default Login;
